import { DateTime } from "luxon";
import { useEffect } from "react";

import {
  ABU_ONLINE_PROD_GEM_FACTSHEET_ENDPOINT,
  ABU_ONLINE_PROD_GEM_FUNDLIST_ENDPOINT,
  ABU_ONLINE_PROD_GEM_GRAPHICDATA_PERFORMANCE_ENDPOINT,
  COLOR,
  DEV_GEM_FACTSHEET_ENDPOINT,
  DEV_GEM_FUNDLIST_ENDPOINT,
  DEV_GEM_GRAPHICDATA_PERFORMANCE_ENDPOINT,
  ERSTE_REAL_ESTATE_FACTSHEET_ENDPOINT,
  ERSTE_REAL_ESTATE_FUNDLIST_ENDPOINT,
  ERSTE_REAL_ESTATE_GRAPHICDATA_PERFORMANCE_ENDPOINT,
  PROD_GEM_FACTSHEET_ENDPOINT,
  PROD_GEM_FUNDLIST_ENDPOINT,
  PROD_GEM_GRAPHICDATA_PERFORMANCE_ENDPOINT,
  UAT_GEM_FACTSHEET_ENDPOINT,
  UAT_GEM_FUNDLIST_ENDPOINT,
  UAT_GEM_GRAPHICDATA_PERFORMANCE_ENDPOINT,
} from "./config";

import {
  Application,
  DepositData,
  EndpointConfiguration,
  Environment,
  Fund,
  GraphicDataPerformance,
  GraphicDataPerformanceJson,
  JSONFactsheetAPI,
  JSONFundlistAPI,
  Locale,
  PerformanceData,
} from "./types";

import localization, { Localization } from "./localization";

import type { ChartData } from "chart.js";
import deLocale from "date-fns/locale/de";
import skLocale from "date-fns/locale/sk";
import enLocale from "date-fns/locale/en-US";

import { deDE, enUS } from "@mui/x-date-pickers/locales";
import { skSK, roRO } from "@mui/material/locale";

const useEvent = (event: string, handler: () => void, passive = false) => {
  useEffect(() => {
    // initiate the event handler
    window.addEventListener(event, handler, passive);
    // this will clean up the event every time the component is re-rendered
    return function cleanup() {
      window.removeEventListener(event, handler);
    };
  });
};

const setupHttpCall = (environment: Environment, url: string, parameters: URLSearchParams) => {
  if (environment === Environment.LOCAL) {
    parameters.set("FILTER_MAIN_SC", "true");
    parameters.set("TIMERANGE_MONTHS", "120");
  }
  return `${url}?${parameters}`;
};

const setupRequestHeaders: (environment: Environment, localeString: string) => HeadersInit = (
  environment: Environment,
  localeString: string,
) => {
  if (environment === Environment.LOCAL) {
    return {
      "Accept-Language": localeString,
      Accept: "application/json",
      username: "PUBLIC",
      applicationId: "6",
      moduleCode: "moduleCode",
    } as HeadersInit;
  }
  return {
    "Accept-Language": localeString,
    Accept: "application/json",
  } as HeadersInit;
};

const determineEndpointUrlByApplicationConfig = (endpointConfig: EndpointConfiguration): string => {
  let endpoint: string = endpointConfig.devUrl;
  switch (endpointConfig.currentEnvironment) {
    case Environment.LOCAL:
      endpoint = endpointConfig.localUrl;
      break;

    case Environment.DEV:
      endpoint = endpointConfig.devUrl;
      break;

    case Environment.UAT:
      endpoint = endpointConfig.uatUrl;
      break;

    case Environment.PROD:
      endpoint = endpointConfig.defaultProdUrl;

      switch (endpointConfig.currentApplication) {
        case Application.ABU_ONLINE:
          endpoint = endpointConfig.abuOnlineUrl;
          break;

        case Application.ERSTE_REAL_ESTATE:
          endpoint = endpointConfig.ersteRealEstateUrl;
          break;
      }
      break;
  }
  return endpoint;
};

const fetchFundlist = async (
  locale: Locale,
  environment: Environment,
  application: Application,
): Promise<Fund[]> => {
  const { localeString } = localization[locale];
  const endpoint: string = determineEndpointUrlByApplicationConfig({
    localUrl: DEV_GEM_FUNDLIST_ENDPOINT,
    devUrl: DEV_GEM_FUNDLIST_ENDPOINT,
    uatUrl: UAT_GEM_FUNDLIST_ENDPOINT,
    defaultProdUrl: PROD_GEM_FUNDLIST_ENDPOINT,
    abuOnlineUrl: ABU_ONLINE_PROD_GEM_FUNDLIST_ENDPOINT,
    ersteRealEstateUrl: ERSTE_REAL_ESTATE_FUNDLIST_ENDPOINT,
    currentApplication: application,
    currentEnvironment: environment,
  });

  const response = await fetch(endpoint, {
    method: "GET",
    headers: setupRequestHeaders(environment, localeString),
  });

  const fundListJSON: JSONFundlistAPI = await response.json();
  return fundListJSON.DATA.map((fund, i) => ({
    id: i,
    masterISIN: fund.SHARES.find((e) => e.MASTER_SHARE_CERTIFICATE === true)!.SHARE_ISIN, // eslint-disable-line @typescript-eslint/no-non-null-assertion
    label: fund.FUND_NAME_LONG,
    isins: fund.SHARES.map((e) => e.SHARE_ISIN).join(", "),
    investmentCompanyShort: fund.INVESTMENT_COMPANY_SHORT,
  }));
};

const fetchPerformance = async (
  locale: Locale,
  environment: Environment,
  masterISIN: string,
  fromDate: DateTime,
  toDate: DateTime,
  application: Application,
): Promise<GraphicDataPerformance[]> => {
  const { localeString } = localization[locale];

  const endpoint: string = determineEndpointUrlByApplicationConfig({
    localUrl: DEV_GEM_GRAPHICDATA_PERFORMANCE_ENDPOINT,
    devUrl: DEV_GEM_GRAPHICDATA_PERFORMANCE_ENDPOINT,
    uatUrl: UAT_GEM_GRAPHICDATA_PERFORMANCE_ENDPOINT,
    defaultProdUrl: PROD_GEM_GRAPHICDATA_PERFORMANCE_ENDPOINT,
    abuOnlineUrl: ABU_ONLINE_PROD_GEM_GRAPHICDATA_PERFORMANCE_ENDPOINT,
    ersteRealEstateUrl: ERSTE_REAL_ESTATE_GRAPHICDATA_PERFORMANCE_ENDPOINT,
    currentApplication: application,
    currentEnvironment: environment,
  });

  const response = await fetch(
    setupHttpCall(
      environment,
      endpoint,
      new URLSearchParams({
        ISIN: masterISIN,
        DAT_VON: fromDate.toMillis().toString(),
        DAT_BIS: toDate.toMillis().toString(),
      }),
    ),
    {
      method: "GET",
      headers: setupRequestHeaders(environment, localeString),
    },
  );

  const graphicDataJson: GraphicDataPerformanceJson = await response.json();
  return graphicDataJson.map((json) => {
    return {
      chart: {
        series: json.chart.series.map((jsonSeries) => {
          return {
            name: jsonSeries.name,
            data: jsonSeries.data.map(
              (jsonSeriesData) =>
                ({
                  timestamp: jsonSeriesData[0],
                  performance: jsonSeriesData[1],
                  fee: jsonSeriesData[8],
                } as PerformanceData),
            ),
          };
        }),
      },
    } as GraphicDataPerformance;
  });
};

const fetchFactsheetWarnings = async (
  locale: Locale,
  environment: Environment,
  isin: string,
  application: Application,
): Promise<JSONFactsheetAPI> => {
  let factSheet: JSONFactsheetAPI;

  if (environment === Environment.LOCAL) {
    factSheet = require("./GEM_FACTSHEET.json");
  } else {
    const { localeString } = localization[locale];

    const endpoint: string = determineEndpointUrlByApplicationConfig({
      localUrl: "NOT USED - Cross Origin error prevents endpoint from working",
      devUrl: DEV_GEM_FACTSHEET_ENDPOINT,
      uatUrl: UAT_GEM_FACTSHEET_ENDPOINT,
      defaultProdUrl: PROD_GEM_FACTSHEET_ENDPOINT,
      abuOnlineUrl: ABU_ONLINE_PROD_GEM_FACTSHEET_ENDPOINT,
      ersteRealEstateUrl: ERSTE_REAL_ESTATE_FACTSHEET_ENDPOINT,
      currentApplication: application,
      currentEnvironment: environment,
    });

    const response = await fetch(
      `${endpoint}?` +
        new URLSearchParams({
          ISIN: isin,
        }),
      {
        method: "GET",
        headers: setupRequestHeaders(environment, localeString),
      },
    );
    factSheet = await response.json();
  }

  return factSheet;
};

const calculatePerformance = (
  performanceData: PerformanceData[],
  fee: number,
  depositData: DepositData,
  locale: Locale,
): (number | null)[] => {
  const { timezone } = localization[locale];

  let t = 0,
    p = 0,
    v = 0,
    i = 0,
    k = 0;

  const notNullIndices: number[] = [];

  const calculatedPerformanceData = performanceData.map((row) => {
    const date = DateTime.fromMillis(row.timestamp).setZone(timezone).startOf("day");
    const dateIndex = depositData.dates.findIndex((_) => _.toMillis() === date.toMillis());

    const depositAtDate = Number(depositData.deposit[dateIndex]);
    const performance = row.performance;

    if (t === 0) {
      p = depositAtDate / (1 + fee);
      v = performance;
      t = 1;
    } else {
      p = p * (performance / v) + (depositAtDate - i) / (1 + fee);
      v = performance;
    }
    i = depositAtDate;
    notNullIndices.push(dateIndex);

    return p;
  });

  const data: (number | null)[] = depositData.deposit.map((_, index: number) => {
    // eslint-disable-line @typescript-eslint/no-explicit-any
    if (notNullIndices.includes(index)) {
      const dataPoint = calculatedPerformanceData[k];
      k++;
      return dataPoint;
    } else {
      return null;
    }
  });

  return data;
};

const calculateDepositChart = (
  locale: Locale,
  startDate: DateTime,
  endDate: DateTime,
  initDeposit: number,
  monthlyRate: number,
  indexing: number,
): ChartData<"line"> & {
  labels: string[];
} => {
  const { depositChartLabel } = localization[locale];

  let incrementalDate: DateTime = startDate;
  let month: number;
  let monthCounter = 0;
  let depositTotal = initDeposit;

  const chartData: ChartData<"line"> & {
    labels: string[];
  } = {
    labels: [],
    datasets: [
      {
        label: depositChartLabel,
        data: [],
        borderColor: "transparent",
        pointRadius: 0,
        borderWidth: 1,
        fill: true,
        backgroundColor: COLOR.blueTransparent,
      },
    ],
  };

  while (incrementalDate <= endDate) {
    if (monthCounter !== 0) {
      depositTotal =
        depositTotal + monthlyRate * (1 + indexing / 100) ** Math.floor(monthCounter / 12);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    month = incrementalDate.month;
    while (incrementalDate.month === month && incrementalDate <= endDate) {
      chartData.datasets[0].data.push(depositTotal);
      chartData.labels!.push(incrementalDate.toISO()); // eslint-disable-line @typescript-eslint/no-non-null-assertion
      incrementalDate = incrementalDate.plus({ days: 1 });
    }
    monthCounter++;
  }
  return chartData;
};

function getPerformanceData(graphicDataPerformance: GraphicDataPerformance[]): PerformanceData[] {
  if (graphicDataPerformance[0].chart.series.length === 1) {
    // some have 1 series with lots of data
    return graphicDataPerformance[0].chart.series[0].data;
  } else {
    // others have lots of series with 2 data, one of which gets filtered out
    return graphicDataPerformance[0].chart.series
      .filter(function (elem) {
        return !elem.name.startsWith("Benchmark"); // Benchmark indicates Benchmark data
      })
      .flatMap((series) => series.data);
  }
}

function convertFeeToNumber(feeString: string): number {
  return parseFloat(feeString.replace(",", "."));
}

function changeLabelLanguageToSk(label: string): string {
  return label
    .replace("Jan", "jan")
    .replace("Feb", "feb")
    .replace("Mar", "mar")
    .replace("Apr", "apr")
    .replace("May", "máj")
    .replace("Jun", "jún")
    .replace("Jul", "júl")
    .replace("Aug", "aug")
    .replace("Sep", "sep")
    .replace("Oct", "okt")
    .replace("Nov", "nov")
    .replace("Dec", "dec");
}

const getDateFnsLocaleForConfig = (locale: Locale) => {
  switch (locale) {
    case Locale.skSK:
      return skLocale;
    case Locale.deAT:
    case Locale.deDE:
      return deLocale;
    case Locale.enAT:
    case Locale.enDE:
    case Locale.enSK:
    case Locale.roRO:
    case Locale.enRO:
    default:
      return enLocale;
  }
};

function isNotSlovakiaDomain(locale: Locale): boolean {
  return locale !== Locale.enSK && locale !== Locale.skSK;
}

function isRomanianDomain(locale: Locale): boolean {
  return locale === Locale.enRO || locale === Locale.roRO;
}

function getMuixLocaleForAppLocale(locale: Locale) {
  switch (locale) {
    case Locale.deAT:
    case Locale.deDE:
      return deDE;
    case Locale.enAT:
    case Locale.enDE:
    case Locale.enSK:
      return enUS;
    case Locale.skSK:
      return skSK;
    case Locale.roRO:
      return roRO;
    case Locale.enRO:
      return enUS;
    default:
      return deDE; // Default to German locale if none matches
  }
}

export enum InvestmentCompanyShort {
  EAM_RO = "EAM RO",
}

function getCurrencyForInvestmentCompany({
                                                  investmentCompanyShort,
                                                  locale,
                                                }: {
  investmentCompanyShort: string | undefined | null;
  locale: Localization;
}): string {
  return investmentCompanyShort === InvestmentCompanyShort.EAM_RO ? locale.currencyRO : locale.currency;
}

function getCurrenySignForInvestmentCompany({
                                                     investmentCompanyShort,
                                                     locale,
                                                   }: {
  investmentCompanyShort: string | undefined | null;
  locale: Localization;
}): string {
  return investmentCompanyShort === InvestmentCompanyShort.EAM_RO
    ? locale.currencySignRO
    : locale.currencySign;
}

export {
  useEvent,
  fetchFundlist,
  fetchPerformance,
  fetchFactsheetWarnings,
  calculatePerformance,
  calculateDepositChart,
  getPerformanceData,
  changeLabelLanguageToSk,
  getDateFnsLocaleForConfig,
  isNotSlovakiaDomain,
  isRomanianDomain,
  getMuixLocaleForAppLocale,
  getCurrencyForInvestmentCompany,
  getCurrenySignForInvestmentCompany,
};
